import type {
  ApiClientDocument,
  MembershipDocument,
  WorkspaceDocument,
  WorkspaceMembershipRole,
} from '@myplayer/schema';
import {
  IconCheck,
  IconEdit,
  IconExternalLink,
  IconKey,
  IconRefresh,
  IconShieldOff,
  IconTrash,
} from '@myplayer/ui';
import type { ReactNode } from 'react';
import {
  CONTEXT_MENU_DIVIDER,
  type ContextMenuActionItem,
  type ContextMenuEntry,
} from './components/ui/context-menu.js';
import { formatDomainStatusLabel, type CustomDomainItem } from './domains-dns-records.js';
import type { GoogleAnalyticsIntegrationState } from './integrations-helpers.js';

export function copyMenuItem(
  id: string,
  label: string,
  copyText: string,
  copyToast?: string,
  icon: ReactNode = <IconCheck />,
): ContextMenuActionItem {
  return {
    id,
    label,
    icon,
    copyText,
    copyToast: copyToast ?? `Copied ${copyText}.`,
  };
}

export function externalUrlMenuItem(
  id: string,
  label: string,
  externalUrl: string,
): ContextMenuActionItem {
  return {
    id,
    label,
    icon: <IconExternalLink />,
    externalUrl,
  };
}

export function buildCustomDomainContextMenuItems(params: {
  readonly domain: CustomDomainItem;
  readonly canVerifyDomains: boolean;
  readonly canManageDomains: boolean;
  readonly submitting: boolean;
  readonly onVerify: (domain: CustomDomainItem) => void;
  readonly onRecheck?: ((domain: CustomDomainItem) => void) | undefined;
  readonly onEdit: (domain: CustomDomainItem) => void;
  readonly onRequestRemove?: ((domain: CustomDomainItem) => void) | undefined;
}): ContextMenuEntry[] {
  const {
    domain,
    canVerifyDomains,
    canManageDomains,
    submitting,
    onVerify,
    onRecheck,
    onEdit,
    onRequestRemove,
  } = params;
  const statusLabel = formatDomainStatusLabel(domain.status, domain.tlsStatus);
  const showRecheckButton = domain.status === 'FAILING' || statusLabel === 'Active';
  const token = domain.verificationToken.trim();
  const cname = domain.cnameTarget?.trim() ?? '';

  return [
    canVerifyDomains && {
      id: 'recheck-domain',
      label: 'Re-check DNS & HTTPS',
      icon: <IconRefresh />,
      disabled: submitting,
      onSelect: () => (showRecheckButton ? (onRecheck ?? onVerify)(domain) : onVerify(domain)),
    },
    canManageDomains && {
      id: 'edit-domain',
      label: 'Edit domain',
      icon: <IconEdit />,
      disabled: submitting,
      onSelect: () => onEdit(domain),
    },
    CONTEXT_MENU_DIVIDER,
    copyMenuItem(
      'copy-hostname',
      `Copy hostname (${domain.hostname})`,
      domain.hostname,
      `Copied ${domain.hostname}.`,
    ),
    token.length > 0 &&
      copyMenuItem(
        'copy-txt-token',
        'Copy TXT verification token',
        token,
        'Copied TXT verification token.',
      ),
    cname.length > 0 &&
      copyMenuItem('copy-cname-target', 'Copy CNAME target', cname, 'Copied CNAME target.'),
    CONTEXT_MENU_DIVIDER,
    canManageDomains &&
      onRequestRemove !== undefined && {
        id: 'remove-domain',
        label: 'Remove domain',
        icon: <IconTrash />,
        destructive: true,
        disabled: submitting,
        onSelect: () => onRequestRemove(domain),
      },
  ];
}

export function buildDefaultDomainContextMenuItems(
  params: string | { readonly effectiveOrigin: string },
): ContextMenuEntry[] {
  const effectiveOrigin = typeof params === 'string' ? params : params.effectiveOrigin;
  return [
    copyMenuItem(
      'copy-default-origin',
      `Copy default web address (${effectiveOrigin})`,
      effectiveOrigin,
      `Copied ${effectiveOrigin}.`,
    ),
  ];
}

export function buildMemberContextMenuItems(params: {
  readonly member: MembershipDocument;
  readonly isSelf: boolean;
  readonly currentRole: WorkspaceMembershipRole;
  readonly canManageMembers: boolean;
  readonly submitting: boolean;
  readonly onStartEditRole: (m: MembershipDocument, r: WorkspaceMembershipRole) => void;
  readonly onDeactivate: (m: MembershipDocument) => void;
}): ContextMenuEntry[] {
  const {
    member,
    isSelf,
    currentRole,
    canManageMembers,
    submitting,
    onStartEditRole,
    onDeactivate,
  } = params;
  const canMutate = canManageMembers && !isSelf && member.status !== 'REVOKED';

  return [
    canMutate && {
      id: 'edit-member-role',
      label: 'Edit role',
      icon: <IconEdit />,
      disabled: submitting,
      onSelect: () => onStartEditRole(member, currentRole),
    },
    CONTEXT_MENU_DIVIDER,
    copyMenuItem(
      'copy-member-email',
      `Copy email (${member.email})`,
      member.email,
      `Copied ${member.email}.`,
    ),
    copyMenuItem('copy-member-uid', 'Copy member UID', member.uid, `Copied UID ${member.uid}.`),
    CONTEXT_MENU_DIVIDER,
    canMutate && {
      id: 'deactivate-member',
      label: 'Deactivate member',
      icon: <IconTrash />,
      destructive: true,
      disabled: submitting,
      onSelect: () => onDeactivate(member),
    },
  ];
}

export function buildWorkspaceContextMenuItems(params: {
  readonly ws: WorkspaceDocument;
  readonly isCurrent: boolean;
  readonly canUpdateWorkspace: boolean;
  readonly submitting: boolean;
  readonly onStartRename: (ws: WorkspaceDocument) => void;
  readonly onArchive: (ws: WorkspaceDocument) => void;
  readonly onSelectWorkspace?: ((ws: WorkspaceDocument) => void) | undefined;
}): ContextMenuEntry[] {
  const {
    ws,
    isCurrent,
    canUpdateWorkspace,
    submitting,
    onStartRename,
    onArchive,
    onSelectWorkspace,
  } = params;
  const isActive = ws.status !== 'ARCHIVED';

  return [
    !isCurrent &&
      isActive &&
      onSelectWorkspace !== undefined && {
        id: 'switch-workspace',
        label: 'Switch to this workspace',
        icon: <IconCheck />,
        disabled: submitting,
        onSelect: () => onSelectWorkspace(ws),
      },
    canUpdateWorkspace &&
      isActive && {
        id: 'rename-workspace',
        label: 'Rename workspace',
        icon: <IconEdit />,
        disabled: submitting,
        onSelect: () => onStartRename(ws),
      },
    CONTEXT_MENU_DIVIDER,
    copyMenuItem(
      'copy-workspace-name',
      `Copy workspace name (${ws.name})`,
      ws.name,
      `Copied ${ws.name}.`,
    ),
    copyMenuItem(
      'copy-workspace-id',
      `Copy workspace ID (${ws.workspaceId})`,
      ws.workspaceId,
      `Copied workspace ID ${ws.workspaceId}.`,
    ),
    CONTEXT_MENU_DIVIDER,
    canUpdateWorkspace &&
      !isCurrent &&
      isActive && {
        id: 'archive-workspace',
        label: 'Archive workspace',
        icon: <IconTrash />,
        destructive: true,
        disabled: submitting,
        onSelect: () => onArchive(ws),
      },
  ];
}

export function buildApiKeyContextMenuItems(params: {
  readonly client: ApiClientDocument;
  readonly canManageApiClients: boolean;
  readonly submitting: boolean;
  readonly onRevoke: (clientId: string) => void;
}): ContextMenuEntry[] {
  const { client, canManageApiClients, submitting, onRevoke } = params;
  return [
    copyMenuItem(
      'copy-key-prefix',
      `Copy key prefix (${client.keyPrefix})`,
      client.keyPrefix,
      `Copied key prefix ${client.keyPrefix}.`,
      <IconKey />,
    ),
    copyMenuItem(
      'copy-key-name',
      `Copy key name (${client.name})`,
      client.name,
      `Copied ${client.name}.`,
    ),
    copyMenuItem(
      'copy-client-id',
      'Copy client ID',
      client.clientId,
      `Copied client ID ${client.clientId}.`,
    ),
    CONTEXT_MENU_DIVIDER,
    canManageApiClients &&
      client.status === 'ACTIVE' && {
        id: 'revoke-api-key',
        label: 'Revoke API key',
        icon: <IconShieldOff />,
        destructive: true,
        disabled: submitting,
        onSelect: () => onRevoke(client.clientId),
      },
  ];
}

export function buildGoogleAnalyticsContextMenuItems(params: {
  readonly gaState: GoogleAnalyticsIntegrationState;
  readonly canManageIntegrations: boolean;
  readonly busy: boolean;
  readonly onOpenEditModal: () => void;
  readonly onToggleEnabled: (nextEnabled: boolean) => void;
  readonly onSendTestEvent: () => void;
  readonly onDisconnect: () => void;
  readonly onRemoveTile?: (() => void) | undefined;
}): ContextMenuEntry[] {
  const {
    gaState,
    canManageIntegrations,
    busy,
    onOpenEditModal,
    onToggleEnabled,
    onSendTestEvent,
    onDisconnect,
    onRemoveTile,
  } = params;
  const isConfigured = gaState.measurementId.length > 0 && gaState.hasApiSecret;

  return [
    canManageIntegrations && {
      id: 'edit-ga',
      label: isConfigured ? 'Edit Google Analytics' : 'Connect Google Analytics',
      icon: <IconEdit />,
      disabled: busy,
      onSelect: onOpenEditModal,
    },
    canManageIntegrations &&
      isConfigured && {
        id: 'toggle-ga',
        label: gaState.enabled ? 'Turn scan forwarding off' : 'Turn scan forwarding on',
        icon: <IconCheck />,
        disabled: busy,
        onSelect: () => onToggleEnabled(!gaState.enabled),
      },
    canManageIntegrations &&
      isConfigured && {
        id: 'test-ga-event',
        label: 'Send test event',
        icon: <IconRefresh />,
        disabled: busy,
        onSelect: onSendTestEvent,
      },
    CONTEXT_MENU_DIVIDER,
    gaState.measurementId.length > 0 &&
      copyMenuItem(
        'copy-measurement-id',
        `Copy Measurement ID (${gaState.measurementId})`,
        gaState.measurementId,
        `Copied Measurement ID ${gaState.measurementId}.`,
      ),
    CONTEXT_MENU_DIVIDER,
    canManageIntegrations &&
      (isConfigured || onRemoveTile !== undefined) && {
        id: 'disconnect-ga',
        label: isConfigured ? 'Disconnect Google Analytics' : 'Remove tile',
        icon: <IconTrash />,
        destructive: true,
        disabled: busy,
        onSelect: isConfigured ? onDisconnect : () => onRemoveTile?.(),
      },
  ];
}
